import React, {FC, Fragment, useRef, useState} from 'react';
import styled from 'styled-components';

import {globalWindow} from '../../../../../libs/shared/helpers/src/browser/browserHelpers';
import {ChromelessButton} from '../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {
  DropdownItem,
  DropdownItemProps,
} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemCheckbox';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis, flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {
  AnalyticsFilterTypesEnum,
  AnalyticsSavedViewFiltersModel,
  isTermWithoutIds,
  isTermWithoutTags,
} from '../../../../models/api/analyticsSavedViewModel';
import {fetchFullSavedViewAsyncAction} from '../../../../store/panels/analytics/views/analyticsPanelViewsActions';
import {AnalyticsSavedView} from '../../../../store/panels/analytics/views/analyticsPanelViewsState';
import {renderFilterContent, renderFilterIds} from '../../../helpers/analyticsFiltersHelpers';
import {getFilterTypeFromTerm, isResourceKey} from '../../../helpers/analyticsSavedViewsHelpers';
import {analyticsFiltersConfig} from '../../filters/common/analyticsFiltersConfig';
import {TabPinIcon} from '../../tabs/tabPinIcon';
import {AnalyticsResourceMap} from './reportHeaderSavedViewsDropdown';
import {SavedViewsDropdownItemPreview} from './reportHeaderSavedViewsDropdownItemPreview';

/*
 * Props.
 */

interface SavedViewsDropdownItemProps extends DropdownItemProps {
  item: AnalyticsSavedView;
  resourceMaps: AnalyticsResourceMap;
}

/*
 * Styles.
 */

const DropdownSavedViewItemWrapper = styled.div`
  grid-area: content;
  display: flex;
  flex-direction: column;
  min-width: 0;
  max-width: 100%;
  margin-left: 4px;
`;

const DropdownSavedViewNameWrapper = styled.div`
  ${flexCenter}
  height: 16px;
  margin-top: 3px;
  margin-bottom: 8px;
`;

const DropdownSavedViewName = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  line-height: ${fontSizes.large};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.medium};
  padding-right: 5px;
  ${ellipsis()}
`;

const DropdownSavedViewFiltersWrapper = styled.div`
  height: 16px;
  display: flex;
  align-items: center;
  ${ellipsis()}
  > * {
    min-width: 0;
    &:not(:last-child) {
      margin-right: 4px;
    }
  }
`;

/*
 * Component.
 */

export const SavedViewsDropdownItem: FC<SavedViewsDropdownItemProps> = ({
  item,
  resourceMaps,
  ...dropdownItemProps
}) => {
  const dispatch = useAppDispatch();

  // We split these 2 as we want the pin icon to be displayed on hover without delay.
  const [isPreviewOpened, setIsPreviewOpened] = useState(false);
  const [isPinIconDisplayed, setIsPinIconDisplayed] = useState(false);

  // Only display the preview after 500ms so we don't fire too many /full calls.
  const [displayPreviewTimeout, setDisplayTimeoutId] = useState<number | undefined>(undefined);

  const dropdownItemRef = useRef<HTMLDivElement>(null);

  const showDropdownItemPreview = isPreviewOpened && item.filters.terms.length !== 0;

  return (
    <ChromelessButton
      ref={dropdownItemRef}
      isOpen={isPreviewOpened}
      onMouseEnter={() => {
        setIsPinIconDisplayed(true);

        globalWindow.clearTimeout(displayPreviewTimeout);
        setDisplayTimeoutId(
          globalWindow.setInstrumentedTimeout(() => {
            setIsPreviewOpened(true);
            if (!item.metadata.hasAllInboxes) {
              dispatch(fetchFullSavedViewAsyncAction(item.id));
            } // Fetching the full view if we're missing some inboxes.
          }, 500),
        );
      }}
      onMouseLeave={() => {
        globalWindow.clearTimeout(displayPreviewTimeout);
        setIsPreviewOpened(false);
        setIsPinIconDisplayed(false);
      }}
    >
      <DropdownItem
        {...dropdownItemProps}
        renderRightContent={() => (
          <DropdownItemCheckbox isDisabled={item.isPinned} isChecked={item.isDisplayed} />
        )}
      >
        <DropdownSavedViewItemWrapper>
          <DropdownSavedViewNameWrapper>
            <DropdownSavedViewName>{item.name}</DropdownSavedViewName>
            {renderPinIconOnHover(item, isPinIconDisplayed)}
          </DropdownSavedViewNameWrapper>
          {renderItemFilters(item.filters, resourceMaps)}
        </DropdownSavedViewItemWrapper>
      </DropdownItem>
      {showDropdownItemPreview && (
        <SavedViewsDropdownItemPreview anchor={dropdownItemRef} item={item} resourceMaps={resourceMaps} />
      )}
    </ChromelessButton>
  );
};

/*
 * Helpers.
 */

const renderPinIconOnHover = (item: AnalyticsSavedView, isHovered: boolean) =>
  (isHovered || item.isPinned) && <TabPinIcon isPinned={item.isPinned} view={item} />;

// @TODO: We must "join" different filters - otherwise ellipsis does not work.
const renderItemFilters = (filters: AnalyticsSavedViewFiltersModel, resourceMaps: AnalyticsResourceMap) => (
  <DropdownSavedViewFiltersWrapper>
    {filters.terms.map((term) => {
      if (isTermWithoutTags(term)) {
        return (
          <Fragment key={term.type}>
            {renderFilterContent(
              analyticsFiltersConfig[AnalyticsFilterTypesEnum.WITHOUT_TAGS].name,
              analyticsFiltersConfig[AnalyticsFilterTypesEnum.WITHOUT_TAGS].iconName,
              true,
            )}
          </Fragment>
        );
      }

      const filterType = getFilterTypeFromTerm(term);
      if (!isResourceKey(filterType)) {
        return null;
      }

      return (
        <Fragment key={filterType}>
          {renderFilterIds({
            ids: isTermWithoutIds(term) ? [] : term.ids,
            itemsMap: resourceMaps[filterType],
            type: filterType,
            iconName: mapFilterTypeToIcon(filterType),
            shouldUseAll: false,
            shouldShrink: true,
            maxItemsCount: 1,
          })}
        </Fragment>
      );
    })}
  </DropdownSavedViewFiltersWrapper>
);

const mapFilterTypeToIcon = (type: AnalyticsFilterTypesEnum) => {
  switch (type) {
    case AnalyticsFilterTypesEnum.TAGS:
      return `tagSmall`;
    case AnalyticsFilterTypesEnum.EXCLUDE_TAGS:
      return `untag`;
    // Channels, inboxes and teammates have per item icons, and don't need filter icon.
    default:
      return undefined;
  }
};
